import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/outline";
import { useVirtualizer } from "@tanstack/react-virtual";
import { PlusIcon, SparklesIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import CreateContextMenu from "../CreateContextMenu";
import DroppableZone from "../DragAndDrop/DroppableZone";
import { useInitialEnterAnimation } from "../hooks/useInitialEnterAnimation";
import useNewItemActions from "../NewItemButton/hooks/useNewItemActions";
import { ITEM_TYPE_GROUPS, ITEM_TYPES, NewItemEntityType } from "../NewItemButton/new-item-types";
import NewItemPopoverContent from "../NewItemButton/NewItemPopoverContent";
import { RegistryItem, SortDirection, SortField } from "../types";
import { useUnifiedRegistry } from "../unified-registry-context";
import { getEntityColor, getTypeIcon } from "../utils";

import ListItem from "./ListItem";
import { ITEM_HEIGHT, VIRTUALIZER_OVERSCAN } from "./utils";

import RequestVolumeTail from "@/components/RequestVolumeTail";
import SmartTableCreateDialog from "@/components/SmartTableCreateDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { useRbac } from "@/providers/RbacProvider";

const PLACEHOLDER_LABEL = "New table, prompt, or more";

const CreatePlaceholderRow: React.FC<{ currentFolderId: number | null }> = ({
  currentFolderId,
}) => {
  const [open, setOpen] = useState(false);
  const store = useUnifiedRegistry();
  const { actions, isLoading, loadingType, smartTableCreateDialogProps } = useNewItemActions(
    currentFolderId ?? undefined,
    store.createInNewTab,
  );
  const { isCreateDisabled } = useRbac();

  const handleItemClick = async (type: NewItemEntityType) => {
    await actions[type]?.();
    setOpen(false);
  };

  return (
    <>
      <SmartTableCreateDialog {...smartTableCreateDialogProps} />
      <Popover
        open={open}
        onOpenChange={nextOpen => {
          if (!isLoading) {
            setOpen(nextOpen);
          }
        }}
        modal={store.modal}
      >
        <PopoverTrigger asChild>
          <button
            disabled={isLoading}
            className="group flex w-full items-center border-b border-dashed border-gray-200 px-2 py-2 text-gray-400 transition-colors duration-150 hover:bg-gray-50/80 hover:text-gray-500"
            style={{ paddingRight: "16px" }}
          >
            <div className="mr-2 flex w-5 flex-shrink-0 items-center justify-center">
              <PlusIcon className="h-3.5 w-3.5 text-gray-300 transition-colors group-hover:text-gray-400" />
            </div>
            <div className="flex min-w-0 flex-1 items-center gap-2.5">
              <div className="flex items-center -space-x-1">
                {ITEM_TYPES.slice(0, 5).map(item => (
                  <div
                    key={item.type}
                    className={`flex h-5 w-5 items-center justify-center rounded-full ${getEntityColor(item.type)} ring-2 ring-white transition-transform group-hover:scale-110`}
                  >
                    {getTypeIcon(item.type)}
                  </div>
                ))}
              </div>
              <OverflowDisplayTooltip content={PLACEHOLDER_LABEL}>
                <span className="truncate text-sm font-medium">{PLACEHOLDER_LABEL}</span>
              </OverflowDisplayTooltip>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-80 rounded-xl border border-gray-200 bg-white p-2 shadow-lg"
          side="bottom"
          align="start"
        >
          <NewItemPopoverContent
            groups={ITEM_TYPE_GROUPS}
            onItemClick={handleItemClick}
            isCreateDisabled={isCreateDisabled}
            isLoading={isLoading}
            loadingType={loadingType}
          />
        </PopoverContent>
      </Popover>
    </>
  );
};

const ListRowContent: React.FC<{
  row: VirtualRow;
  currentFolderId: number | null;
  onItemClick?: (item: RegistryItem) => void;
  shouldAnimateIn?: boolean;
}> = ({ row, currentFolderId, onItemClick, shouldAnimateIn = false }) => {
  if (row.type === "create") {
    return <CreatePlaceholderRow currentFolderId={currentFolderId} />;
  }

  if (row.type === "empty") {
    return <div className="h-12" />;
  }

  if (row.type === "header") {
    return (
      <div className="bg-white px-8 pb-2 pt-4">
        <h2 className="flex items-center gap-1 text-sm font-normal text-gray-600">
          {row.semantic && <SparklesIcon className="h-3.5 w-3.5 text-amber-500" />}
          {row.label} <span className="text-gray-400">({row.totalCount})</span>
        </h2>
      </div>
    );
  }

  if (row.item) {
    return <ListItem item={row.item} onItemClick={onItemClick} shouldAnimateIn={shouldAnimateIn} />;
  }

  return null;
};

interface VirtualizedListViewProps {
  items: RegistryItem[];
  textResultCount?: number;
  isLoading?: boolean;
  sortField?: SortField;
  sortDirection?: SortDirection;
  onSort?: (field: SortField) => void;
  currentFolderId: number | null;
  onItemClick?: (item: RegistryItem) => void;
  showBanner?: boolean;
}

interface VirtualRow {
  type: "banner" | "item" | "empty" | "create" | "header";
  item?: RegistryItem;
  label?: string;
  totalCount?: number;
  semantic?: boolean;
  index: number;
}

const VirtualizedListView: React.FC<VirtualizedListViewProps> = observer(
  ({
    items,
    textResultCount = 0,
    isLoading = false,
    sortField = "modified",
    sortDirection = "desc",
    onSort,
    currentFolderId,
    onItemClick,
    showBanner = true,
  }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const virtualContainerRef = useRef<HTMLDivElement>(null);
    const [scrollMargin, setScrollMargin] = useState(0);
    const shouldAnimateIn = useInitialEnterAnimation();
    const registry = useUnifiedRegistry();

    const hasSearchQuery = !!registry.debouncedSearchQuery;
    const semanticCount = items.length - textResultCount;
    const hasSemanticResults = hasSearchQuery && semanticCount > 0;

    const rows = useMemo<VirtualRow[]>(() => {
      const result: VirtualRow[] = [];
      let currentIndex = 0;

      items.forEach((item, i) => {
        if (hasSemanticResults && i === textResultCount) {
          result.push({
            type: "header",
            label: textResultCount > 0 ? "Other relevant results" : "Relevant results",
            totalCount: semanticCount,
            semantic: true,
            index: currentIndex,
          });
          currentIndex += 1;
        }
        result.push({
          type: "item",
          item,
          index: currentIndex,
        });
        currentIndex += 1;
      });

      result.push({
        type: "create",
        index: currentIndex,
      });
      currentIndex += 1;

      result.push({
        type: "empty",
        index: currentIndex,
      });

      return result;
    }, [items, textResultCount, hasSemanticResults, semanticCount]);

    const virtualizer = useVirtualizer({
      count: rows.length,
      getScrollElement: () => scrollContainerRef.current,
      estimateSize: useCallback(
        (index: number) => {
          const row = rows[index];
          if (row?.type === "empty") {
            return 48;
          }
          if (row?.type === "create") {
            return ITEM_HEIGHT;
          }
          if (row?.type === "header") {
            return 48;
          }
          return ITEM_HEIGHT;
        },
        [rows],
      ),
      overscan: VIRTUALIZER_OVERSCAN,
      getItemKey: useCallback(
        (index: number) => {
          const row = rows[index];
          if (!row) {
            return `row-${index}`;
          }
          if (row.type === "header") {
            return `header-${row.label}`;
          }
          return row.item ? `${row.item.type}-${row.item.id}` : `item-${index}`;
        },
        [rows],
      ),
      scrollMargin,
    });

    const hasItems = items.length > 0;
    useLayoutEffect(() => {
      const scrollEl = scrollContainerRef.current;
      const virtualEl = virtualContainerRef.current;
      if (!scrollEl || !virtualEl) {
        return undefined;
      }

      const update = () => {
        const offset =
          virtualEl.getBoundingClientRect().top -
          scrollEl.getBoundingClientRect().top +
          scrollEl.scrollTop;
        setScrollMargin(offset);
      };

      update();
      const resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(scrollEl);
      if (virtualEl.parentElement) {
        resizeObserver.observe(virtualEl.parentElement);
      }

      return () => resizeObserver.disconnect();
    }, [isLoading, hasItems, showBanner]);

    const virtualItems = virtualizer.getVirtualItems();

    if (isLoading) {
      return (
        <div className="px-6">
          <div className="border-t border-gray-200">
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`skeleton-${i}`}
                className="flex items-center border-b border-gray-100 px-6 py-2"
              >
                <div className="flex flex-1 items-center gap-2.5">
                  <div className="h-5 w-5 animate-pulse rounded bg-gray-200" />
                  <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
                </div>
                <div className="w-24 px-4">
                  <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
                </div>
                <div className="w-32 px-4">
                  <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (items.length === 0) {
      return null;
    }

    return (
      <CreateContextMenu currentFolderId={currentFolderId}>
        <div className="relative h-full">
          <DroppableZone id="root-drop-zone" isRootZone />

          <div
            ref={scrollContainerRef}
            className="h-full overflow-auto"
            data-scroll-container
            style={{
              contain: "strict",
            }}
          >
            <div className={registry.modal ? "min-w-full" : "min-w-[1200px]"}>
              {showBanner && <RequestVolumeTail />}

              <div className="sticky top-0 z-20 border-b border-gray-200 bg-white">
                <div
                  className="flex items-center py-2 text-xs font-medium tracking-wider text-gray-500"
                  style={{ paddingLeft: "8px", paddingRight: "16px" }}
                >
                  <div className="mr-2 w-5 flex-shrink-0" />

                  <div
                    className={`flex flex-1 items-center gap-2.5 ${
                      registry.modal ? "min-w-0" : "min-w-[200px]"
                    }`}
                  >
                    <div className="h-5 w-5 flex-shrink-0" />
                    <button
                      className={`flex min-w-0 flex-1 items-center gap-1 text-left transition-colors hover:text-gray-700 ${
                        sortField === "name" ? "text-gray-900" : ""
                      }`}
                      onClick={() => onSort?.("name")}
                      disabled={!onSort}
                    >
                      <span className="truncate">Name</span>
                      {sortField === "name" && (
                        <span className="flex-shrink-0 text-gray-400">
                          {sortDirection === "desc" ? (
                            <ChevronDownIcon className="h-3 w-3" />
                          ) : (
                            <ChevronUpIcon className="h-3 w-3" />
                          )}
                        </span>
                      )}
                    </button>
                  </div>
                  {registry.isFlattened && !registry.modal && (
                    <div className="flex-shrink-0" style={{ width: "192px" }}>
                      <div className="px-4">
                        <span className="block truncate">Path</span>
                      </div>
                    </div>
                  )}
                  {!registry.modal && (
                    <div className="flex-shrink-0" style={{ width: "128px" }}>
                      <div className="px-4">
                        <button
                          className={`flex items-center gap-1 transition-colors hover:text-gray-700 ${
                            sortField === "type" ? "text-gray-900" : ""
                          }`}
                          onClick={() => onSort?.("type")}
                          disabled={!onSort}
                        >
                          <span className="truncate">Type</span>
                          {sortField === "type" && (
                            <span className="flex-shrink-0 text-gray-400">
                              {sortDirection === "desc" ? (
                                <ChevronDownIcon className="h-3 w-3" />
                              ) : (
                                <ChevronUpIcon className="h-3 w-3" />
                              )}
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="flex-shrink-0" style={{ width: "160px" }}>
                    <div className="px-4">
                      <span className="truncate">Created By</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0" style={{ width: "160px" }}>
                    <div className="px-4">
                      <button
                        className={`flex items-center gap-1 transition-colors hover:text-gray-700 ${
                          sortField === "created" ? "text-gray-900" : ""
                        }`}
                        onClick={() => onSort?.("created")}
                        disabled={!onSort}
                      >
                        <span className="truncate">Created</span>
                        {sortField === "created" && (
                          <span className="flex-shrink-0 text-gray-400">
                            {sortDirection === "desc" ? (
                              <ChevronDownIcon className="h-3 w-3" />
                            ) : (
                              <ChevronUpIcon className="h-3 w-3" />
                            )}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex-shrink-0" style={{ width: "160px" }}>
                    <div className="px-4">
                      <button
                        className={`flex items-center gap-1 transition-colors hover:text-gray-700 ${
                          sortField === "modified" ? "text-gray-900" : ""
                        }`}
                        onClick={() => onSort?.("modified")}
                        disabled={!onSort}
                      >
                        <span className="truncate">Modified</span>
                        {sortField === "modified" && (
                          <span className="flex-shrink-0 text-gray-400">
                            {sortDirection === "desc" ? (
                              <ChevronDownIcon className="h-3 w-3" />
                            ) : (
                              <ChevronUpIcon className="h-3 w-3" />
                            )}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="w-14 flex-shrink-0" />
                </div>
              </div>

              <div
                ref={virtualContainerRef}
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: "100%",
                  position: "relative",
                }}
              >
                {virtualItems.map(virtualItem => {
                  const row = rows[virtualItem.index];
                  if (!row) {
                    return null;
                  }

                  return (
                    <div
                      key={virtualItem.key}
                      data-index={virtualItem.index}
                      ref={virtualizer.measureElement}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        transform: `translateY(${virtualItem.start - scrollMargin}px)`,
                      }}
                    >
                      <ListRowContent
                        row={row}
                        currentFolderId={currentFolderId}
                        onItemClick={onItemClick}
                        shouldAnimateIn={shouldAnimateIn}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </CreateContextMenu>
    );
  },
);

export default VirtualizedListView;
